import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Save, Filter, ChevronDown, ChevronRight, Edit, X } from 'react-feather';

interface GlossaryTerm {
  context: string;         // Required: one-line description for LLM
  expansion?: string;      // Optional: for acronyms (DRC -> "Digital Review Copy")
  synonyms: string[];      // Alternative terms for query variants
  category?: string;       // UI organization only
}

interface GlossaryData {
  meta: {
    title: string;
    description: string;
    version: string;
    lastUpdated: string;
  };
  terms: Record<string, GlossaryTerm>;
}

interface LetterGroup {
  letter: string;
  terms: string[];
}

const ITEMS_PER_PAGE = 10;

const categoryDisplayNames: Record<string, string> = {
  'pricing_financial': '💰 Pricing & Financial',
  'catalog_management': '📖 Catalog Management',
  'technical_production': '🔧 Technical & Production',
  'classification_metadata': '📊 Classification & Metadata',
  'edelweiss_platform': '🌼 Edelweiss Platform',
  'publishing_operations': '📚 Publishing Operations',
  'digital_publishing': '💻 Digital Publishing',
  'sales_marketing': '📈 Sales & Marketing',
  'rights_legal': '⚖️ Rights & Legal',
  'printing_production': '🖨️ Printing & Production',
};

const DomainContentManager: React.FC = () => {
  const [glossaryData, setGlossaryData] = useState<GlossaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedLetter, setExpandedLetter] = useState<string>('a');
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [editingTerm, setEditingTerm] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<GlossaryTerm | null>(null);
  const termRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const letterSectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const loadGlossaryData = async () => {
      try {
        const response = await fetch('/api/domain-content/glossary');
        if (!response.ok) {
          throw new Error('Failed to load glossary data');
        }
        const data: GlossaryData = await response.json();
        setGlossaryData(data);
      } catch (error) {
        console.error('Failed to load glossary data:', error);
        setError('Failed to load glossary data');
      } finally {
        setLoading(false);
      }
    };

    loadGlossaryData();
  }, []);

  // Group terms by first letter
  const letterGroups: LetterGroup[] = useMemo(() => {
    if (!glossaryData) return [];
    
    const terms = Object.keys(glossaryData.terms).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    
    const groups: Record<string, string[]> = {};
    
    terms.forEach(term => {
      const firstLetter = term[0].toLowerCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(term);
    });
    
    return Object.keys(groups)
      .sort()
      .map(letter => ({
        letter: letter.toUpperCase(),
        terms: groups[letter]
      }));
  }, [glossaryData]);

  // Available letters (A-Z)
  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const lettersWithTerms = letterGroups.map(group => group.letter);

  // Filter and paginate terms for main content area
  const { filteredTerms, totalPages } = useMemo(() => {
    if (!glossaryData) return { filteredTerms: [], totalPages: 0 };

    const terms = Object.keys(glossaryData.terms).sort((a, b) => 
      a.toLowerCase().localeCompare(b.toLowerCase())
    );

    let filtered = terms.filter(termKey => {
      const term = glossaryData.terms[termKey];
      const matchesSearch = !searchTerm ||
        termKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
        term.context.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedTerms = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return { filteredTerms: paginatedTerms, totalPages };
  }, [glossaryData, searchTerm, selectedCategory, currentPage]);

  const handleLetterClick = (letter: string) => {
    const letterGroup = letterGroups.find(group => group.letter === letter);
    if (!letterGroup || letterGroup.terms.length === 0) return;
    
    const firstTerm = letterGroup.terms[0];
    
    // Update sidebar state - expand the clicked letter section
    setExpandedLetter(letter.toLowerCase());
    setSelectedTerm(firstTerm);
    
    // Scroll sidebar to show the expanded section right below the A-Z letters
    setTimeout(() => {
      const letterSectionElement = letterSectionRefs.current[letter];
      if (letterSectionElement) {
        // Use scrollIntoView with precise positioning - scroll the letter header to the top
        letterSectionElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
    
    // Clear filters to ensure the term is visible in main content
    setSearchTerm('');
    setSelectedCategory('all');
    
    // Find which page the term would be on (after clearing filters)
    const allTermsSorted = Object.keys(glossaryData!.terms).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    const termIndex = allTermsSorted.findIndex(term => term === firstTerm);
    const targetPage = Math.floor(termIndex / ITEMS_PER_PAGE) + 1;
    
    // Navigate to the correct page
    setCurrentPage(targetPage);
    
    // Scroll to the term in main content after a brief delay
    setTimeout(() => {
      const termElement = termRefs.current[firstTerm];
      if (termElement) {
        termElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const handleTermClick = (termKey: string) => {
    setSelectedTerm(termKey);
    const termElement = termRefs.current[termKey];
    if (termElement) {
      termElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  /**
   * Open the edit modal for a specific term
   * @param termKey - The key of the term to edit
   */
  const handleEditTerm = (termKey: string) => {
    if (!glossaryData) return;
    setEditingTerm(termKey);
    setEditFormData({ ...glossaryData.terms[termKey] });
  };

  /**
   * Close the edit modal and reset form data
   */
  const handleCloseEdit = () => {
    setEditingTerm(null);
    setEditFormData(null);
  };

  /**
   * Save the edited term data to the server and update local state
   */
  const handleSaveEdit = async () => {
    if (!glossaryData || !editingTerm || !editFormData) return;

    const updatedGlossary = {
      ...glossaryData,
      terms: {
        ...glossaryData.terms,
        [editingTerm]: editFormData
      }
    };

    setGlossaryData(updatedGlossary);

    try {
      setSaving(true);
      const response = await fetch('/api/domain-content/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedGlossary),
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      setSaveMessage('✅ Term updated successfully');
      setTimeout(() => setSaveMessage(''), 2000);
      handleCloseEdit();
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveMessage('❌ Save failed');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle changes to form fields in the edit modal
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleFormFieldChange = (field: keyof GlossaryTerm, value: string | string[]) => {
    if (!editFormData) return;
    setEditFormData({
      ...editFormData,
      [field]: value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-lg text-gray-600">Loading domain content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!glossaryData) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Alphabetical Index Sidebar */}
        <div className="w-80 bg-white shadow-lg border-r h-screen sticky top-0 flex flex-col">
          <div className="p-4 border-b bg-blue-50">
            <h3 className="text-lg font-semibold text-gray-800">
              Alphabetical Index
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {Object.keys(glossaryData.terms).length} terms
            </p>
          </div>
          
          {/* A-Z Letter Navigation - STICKY */}
          <div className="p-3 border-b bg-gray-50 sticky top-0 z-10">
            <div className="grid grid-cols-6 gap-1">
              {allLetters.map(letter => {
                const hasTerms = lettersWithTerms.includes(letter);
                return (
                  <button
                    key={letter}
                    onClick={() => handleLetterClick(letter)}
                    disabled={!hasTerms}
                    className={`
                      w-10 h-10 text-sm font-semibold rounded transition-colors
                      ${hasTerms 
                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }
                      ${expandedLetter.toUpperCase() === letter ? 'ring-2 ring-blue-500' : ''}
                    `}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Expandable Letter Sections - SCROLLABLE */}
          <div className="flex-1 overflow-y-auto divide-y" ref={sidebarScrollRef}>
            {letterGroups.map(group => (
              <div 
                key={group.letter} 
                className="border-b"
                ref={el => { letterSectionRefs.current[group.letter] = el; }}
              >
                <button
                  className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 font-medium"
                  onClick={() => setExpandedLetter(
                    expandedLetter === group.letter.toLowerCase() ? '' : group.letter.toLowerCase()
                  )}
                >
                  <span className="flex items-center">
                    <span className="text-blue-600 font-bold text-lg mr-3">{group.letter}</span>
                    <span className="text-gray-600 text-sm">({group.terms.length})</span>
                  </span>
                  {expandedLetter === group.letter.toLowerCase() ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                {expandedLetter === group.letter.toLowerCase() && (
                  <div className="bg-gray-50">
                    {group.terms.map(term => (
                      <button
                        key={term}
                        onClick={() => handleTermClick(term)}
                        className={`
                          w-full px-8 py-2 text-left text-sm hover:bg-blue-50 
                          ${selectedTerm === term ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'}
                        `}
                      >
                        {term.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 max-w-none">
          {/* Header */}
          <div className="bg-white shadow-sm border-b px-6 py-4">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{glossaryData.meta.title}</h1>
                <p className="text-gray-600">{glossaryData.meta.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                {saveMessage && (
                  <span className="text-sm px-3 py-1 rounded-full bg-green-100 text-green-800">
                    {saveMessage}
                  </span>
                )}
                {saving && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search terms or definitions..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {Object.keys(categoryDisplayNames).map(category => (
                  <option key={category} value={category}>
                    {categoryDisplayNames[category]}
                  </option>
                ))}
              </select>

              <span className="text-sm text-gray-500">
                {filteredTerms.length} of {Object.keys(glossaryData.terms).length} terms
              </span>
            </div>
          </div>

          {/* Terms List */}
          <div className="p-6">
            {filteredTerms.length === 0 ? (
              <div className="text-center py-12">
                <Filter className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No terms match your current filters.</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredTerms.map(termKey => {
                  const term = glossaryData.terms[termKey];
                  return (
                    <div
                      key={termKey}
                      ref={el => { termRefs.current[termKey] = el; }}
                      className={`
                        p-4 border rounded-lg hover:shadow-md transition-shadow bg-white
                        ${selectedTerm === termKey ? 'ring-2 ring-blue-500 shadow-md' : ''}
                      `}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg">{termKey.replace(/_/g, ' ')}</h3>
                          {term.expansion && (
                            <span className="text-sm text-blue-600">{term.expansion}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleEditTerm(termKey)}
                          className="text-gray-500 hover:text-blue-600"
                          title="Edit term"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                      <p className="text-gray-600 mt-2 line-clamp-2">{term.context}</p>
                      {term.synonyms?.length > 0 && (
                        <div className="mt-2 text-sm text-gray-500">
                          {term.synonyms.length} synonym{term.synonyms.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 rounded ${
                            currentPage === page
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return <span key={page} className="px-3 py-2">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Term Modal */}
      {editingTerm && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Term: {editingTerm.replace(/_/g, ' ')}
              </h2>
              <button
                onClick={handleCloseEdit}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Context - Required */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Context <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editFormData.context || ''}
                  onChange={(e) => handleFormFieldChange('context', e.target.value)}
                  placeholder="One-line description for query expansion"
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Expansion - For acronyms */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Expansion (for acronyms)
                </label>
                <input
                  type="text"
                  value={editFormData.expansion || ''}
                  onChange={(e) => handleFormFieldChange('expansion', e.target.value)}
                  placeholder="e.g., Digital Review Copy"
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Synonyms */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Synonyms
                </label>
                <input
                  type="text"
                  value={(editFormData.synonyms || []).join(', ')}
                  onChange={(e) => handleFormFieldChange('synonyms',
                    e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  )}
                  placeholder="advance copy, galley, preview"
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Category */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  value={editFormData.category || ''}
                  onChange={(e) => handleFormFieldChange('category', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select category...</option>
                  {Object.entries(categoryDisplayNames).map(([key, display]) => (
                    <option key={key} value={key}>
                      {display}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseEdit}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editFormData.context?.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainContentManager;